// React
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
// Services
import UserService from '../../services/UserService';
import { isLightMode, isRouteAllowedInLightMode } from 'src/services/LightMode';
import { toAbsolutePublicUrl } from 'src/services/PublicUrl';

/////////////////////////////////////
//              Props              //
/////////////////////////////////////

interface ProtectedRouteProps {
    children: React.ReactNode;
    needsLogin?: boolean;
    protectLightMode?: boolean;
}

/////////////////////////////////////
//            Component            //
/////////////////////////////////////

/**
 * ProtectedRoute component to guard routes and ensure authentication.
 * Redirects to login if the user is not authenticated.
 * 
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
    children, 
    needsLogin= true,
    protectLightMode = true
 }) => {

    /////////////////////////////////////
    //              States             //
    /////////////////////////////////////

    const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(undefined);
    const location = useLocation();
    /////////////////////////////////////
    //            LifeCycle            //
    /////////////////////////////////////

    /**
     * Redirect if not permitted page
     */
    if (
        protectLightMode &&
        isLightMode() &&
        !isRouteAllowedInLightMode(location.pathname)
    ) {
        return <Navigate to="/Home" replace />;
    }

    /**
     * Check if the user is authenticated.
     * If not, redirect to login.
     * 
     */
    useEffect(() => {
        const checkAuth = async () => {
            if (!needsLogin) {
                setIsAuthenticated(true);
                return;
            }
            const authenticated = UserService.isAuthenticated();
            if (!authenticated) {
                // Store the current URL in localStorage before redirecting to login
                localStorage.setItem(
                    'postLoginRedirectUri',
                    toAbsolutePublicUrl(`${location.pathname}${location.search}${location.hash}`)
                );
                await UserService.doLogin();
                return;
            } else {
                setIsAuthenticated(true);
            }
        };
        checkAuth();
    }, [location.hash, location.pathname, location.search, needsLogin]);

    /////////////////////////////////////
    //            Content              //
    /////////////////////////////////////

    if (!needsLogin) {
        return <>{children}</>;
    }

    if (!isAuthenticated) {
        return <div></div>;
    }

    return <>{children}</>;
};

export default ProtectedRoute;
